import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CheckCircle, Calendar } from "lucide-react";
import { CoinsIcon } from "@/components/ui/CoinsIcon";
import { formatRewardAmount } from "@/lib/textUtils";

export interface ClientConversionFormData {
  conversionDate: string;
}

interface ClientReferralConvertedModalProps {
  isOpen: boolean;
  companyName: string | null;
  rewardAmount: number;
  onConfirm: (formData: ClientConversionFormData) => void;
  onCancel: () => void;
  isProcessing?: boolean;
}

export function ClientReferralConvertedModal({ 
  isOpen, 
  companyName, 
  rewardAmount,
  onConfirm, 
  onCancel,
  isProcessing = false
}: ClientReferralConvertedModalProps) {
  
  const [formData, setFormData] = useState<ClientConversionFormData>({
    conversionDate: new Date().toISOString().split('T')[0] // Default to today
  });
  const [errors, setErrors] = useState<Partial<ClientConversionFormData>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        conversionDate: new Date().toISOString().split('T')[0]
      });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<ClientConversionFormData> = {};
    
    if (!formData.conversionDate) {
      newErrors.conversionDate = 'Conversion date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  const handleCancel = () => {
    if (isProcessing) return; // Prevent closing when processing
    
    setFormData({
      conversionDate: new Date().toISOString().split('T')[0]
    });
    setErrors({});
    onCancel();
  };


  return (
    <AlertDialog 
      open={isOpen} 
      onOpenChange={(open) => {
        // Only allow closing if not processing and user wants to close
        if (!open && !isProcessing) {
          handleCancel();
        }
      }}
    >
      <AlertDialogContent 
        className="sm:max-w-lg z-[100]"
        overlayClassName="z-[100]"
        {...(isProcessing ? {
          onPointerDownOutside: (e: Event) => e.preventDefault(),
          onEscapeKeyDown: (e: KeyboardEvent) => e.preventDefault()
        } : {})}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <AlertDialogTitle className="space-grotesk text-green-700 text-left">
                Confirm Client Conversion
              </AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className="text-left">
            You are about to mark <strong>{companyName}</strong> as <strong>CONVERTED</strong> to a client.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800 space-grotesk">
              <strong>What this means:</strong> This status indicates that {companyName} has successfully hired a candidate placed through the AiR TAP program and the candidate has passed their probation period.
            </p>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <CoinsIcon className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 space-grotesk">Referral Reward</p>
                <p className="text-lg font-bold text-blue-700 space-grotesk">R{formatRewardAmount(rewardAmount)}</p>
                <p className="text-xs text-blue-600 space-grotesk mt-1">
                  This reward will be automatically granted to the referrer and can be redeemed immediately upon conversion confirmation.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="conversionDate" className="space-grotesk">
              <Calendar className="w-4 h-4 inline mr-2" />
              Conversion Date *
            </Label>
            <Input
              id="conversionDate"
              type="date"
              value={formData.conversionDate}
              onChange={(e) => setFormData(prev => ({ ...prev, conversionDate: e.target.value }))}
              className={`space-grotesk ${errors.conversionDate ? 'border-red-500' : ''}`}
              disabled={isProcessing}
            />
            {errors.conversionDate && (
              <p className="text-sm text-red-600 space-grotesk">{errors.conversionDate}</p>
            )}
          </div>

        </div>

        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isProcessing}
              className="space-grotesk"
            >
              Cancel
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isProcessing}
              className="space-grotesk bg-green-600 hover:bg-green-700"
            >
              {isProcessing ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                'Confirm Conversion'
              )}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
